import {
  ApostleClassId,
  ApostleClassType,
  ApostleSkill,
  ApostleSkillType
} from '@apeiron/apostle'
import { APOSTLE_SKILL_MAX_LEVEL, MAX_APOSTLE_QUANTITY } from '@apeiron/bounty'
import { useFrontendConfigContext } from '@apeiron/config'
import { CurrencyTokenTypeERC20, useCurrencyContext } from '@apeiron/currency'
import {
  ApeironBlueButton,
  formatNumberString,
  INFO_POPUP_TYPE,
  InfoPopup,
  MuiTextfield,
  NextLink,
  useAccountContext,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import { ApeironNumberTextField } from '@apeiron/ui'
import {
  Box,
  Button,
  Slider,
  styled,
  Typography,
  useMediaQuery
} from '@mui/material'
import ApostleClassSelector from '@src/components/apostles/bounty/ApostleClassSelector'
import ApostleSkillSelector from '@src/components/apostles/bounty/ApostleSkillSelector'
import BountyCurrencyDropdown from '@src/components/apostles/bounty/BountyCurrencyDropdown'
import { REGEX_NUMBER, REGEX_WHOLE_NUMBER } from '@src/constants/regex'
import { ROUTE } from '@src/constants/route'
import { useBountyContext } from '@src/contexts/share/BountyContext'
import useBounty from '@src/hooks/contract/useBounty'
import theme from '@src/styles/theme'
import { toApostleClassId, toApostleClassType } from '@src/util/apeiron/apostle'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

const Popup = styled(InfoPopup)`
  max-height: 98%;
  max-width: 98%;
  width: 720px;

  .Custom-Content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
`

const Container = styled(Box)`
  ${props => props.theme.breakpoints.up('res768')} {
    padding: 20px 70px 30px 70px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    padding: 20px 20px 30px 20px;
  }
`

const Row = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;

  &.mobile-full {
    ${props => props.theme.breakpoints.down('res768')} {
      flex-wrap: wrap;
      > p {
        width: 100%;
      }

      > div {
        flex-basis: 100%;
        align-items: end;
      }
    }
  }
`

const RightCol = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  flex-basis: 160px;
  align-items: center;
`

const FlexBox = styled(Box)`
  display: flex;
  position: relative;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
`

const StyledActionButton = styled(ApeironBlueButton)`
  min-width: 120px;
  margin-top: 20px;
`

const StyledSlider = styled(Slider)`
  width: 160px;
  height: 4px;
  color: #5e5e5e;
  min-width: 140px;
  margin-bottom: 4px;
  .MuiSlider-rail {
    background-color: #80807e;
  }

  .MuiSlider-mark {
    display: none;
  }

  .MuiSlider-track {
    background-color: #febe36;
    border: none;
  }

  .MuiSlider-thumb {
    background-color: #ffffff;
    width: 14px;
    height: 14px;
  }

  .MuiSlider-markLabel {
    color: #fff;
    top: 20px;
    transform: translateX(-50%);
  }
`

const StyledApeironTextField = styled(ApeironNumberTextField)`
  text-align: center;
  ${props => props.theme.breakpoints.up('res480')} {
    width: 160px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 160px;
  }

  .MuiInputBase-input {
    border-radius: 2px;
  }

  .MuiInputBase-root {
    padding-right: 0px;
  }

  .MuiInputBase-root.Mui-disabled {
    background-color: #535556;
  }

  .MuiOutlinedInput-input {
    padding-right: 14px;
  }

  .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled {
    background-color: #535556;
    color: #a0a0a0;
    -webkit-text-fill-color: #a0a0a0;
    opacity: 1;
  }

  .MuiFormHelperText-root {
    white-space: nowrap;
    height: 16px;
    direction: rtl;
  }

  .token {
    color: rgba(255, 255, 255, 0.3);
  }
`

const ActionButtonsContainer = styled(Box)`
  display: flex;
  gap: 20px;
  justify-content: center;
`

const ResetButton = styled(Button)`
  color: #3682eb;
  text-transform: none;
  font-weight: 300;
  text-decoration: underline;
  position: absolute;
  left: 100%;
`

const MobileResetButton = styled(ResetButton)`
  position: relative;
  left: 0;
`

const CancelButton = styled(StyledActionButton)`
  color: #ffffff;
  background-color: #525355;

  :hover {
    background-color: #757577;
  }
`

const MaxQuantity = styled(Typography)`
  font-size: 12px;
  line-height: 14px;
  color: #ffffff;
  margin-top: 4px;

  > strong {
    color: #ffbf36;
  }
`

const TotalPrice = styled(Typography)`
  font-size: 12px;
  line-height: 14px;
  color: #ffffff;

  > strong {
    color: #ffbf36;
  }
`

const CheckHereLink = styled(NextLink)`
  color: #ffbf36;
  text-decoration: underline;
`

const MaxHelperText = () => {
  const { t } = useTranslation()

  return (
    <MaxQuantity>
      <Trans
        components={{
          strong: <strong />
        }}
      >
        {t('bounty.apostle.create_dialog.max_quantity', {
          quantity: MAX_APOSTLE_QUANTITY
        })}
      </Trans>
    </MaxQuantity>
  )
}

const Separator = styled(Box)`
  border-top: 2px dashed rgb(128, 128, 126);
  margin: 30px -70px;

  ${props => props.theme.breakpoints.down('res768')} {
    margin: 30px -24px;
  }
`

const FormLabel = styled(Typography)`
  height: 40px;
  display: flex;
  align-items: center;
`

const LevelTextfield = styled(MuiTextfield)`
  .MuiFormLabel-root.Mui-disabled {
    color: #a0a0a0;
  }

  .MuiInputBase-input {
    text-align: center;

    &.MuiOutlinedInput-input.Mui-disabled {
      background-color: #535556;
      color: #a0a0a0;
      -webkit-text-fill-color: #a0a0a0;
      opacity: 1;
    }
  }

  & label.Mui-focused {
    color: white;
  }
  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: white;
    }
  }

  width: 100px;
`

const ErrorText = styled(Typography)`
  color: red;
  font-size: 12px;
  line-height: 14px;
`

const CurrencyNameText = styled(Typography)`
  padding: 0px 10px;
  color: rgba(255, 255, 255, 0.3);
`

const SkillMinLevelField = ({
  control,
  name,
  disabled
}: {
  control: any
  name: string
  disabled: boolean
}) => {
  const { t } = useTranslation()

  const skillRules = {
    required: false,
    max: {
      value: APOSTLE_SKILL_MAX_LEVEL,
      message: t('common.form_validation.max', {
        max: APOSTLE_SKILL_MAX_LEVEL
      })
    },
    min: {
      value: 0,
      message: t('common.form_validation.min', {
        min: 0
      })
    },
    pattern: {
      value: REGEX_WHOLE_NUMBER,
      message: t('common.form_validation.integer')
    }
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={skillRules}
      render={({ field, fieldState: { error } }) => {
        return (
          <LevelTextfield
            {...field}
            value={field.value || ''}
            disabled={disabled}
            label={
              disabled
                ? ''
                : t('bounty.apostle.create_dialog.min_skill_level_short')
            }
            error={!!error}
            placeholder={disabled ? '*' : '1'}
          />
        )
      }}
    />
  )
}

const CreateApostleBountyDialog = () => {
  const {
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    defaultCreateFormValues,
    setDefaultCreateFormValues,
    refetchBounties,
    supportedCurrencies
  } = useBountyContext()

  const { config } = useFrontendConfigContext()

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateBountyFormInputs>({
    defaultValues: {
      currency: supportedCurrencies[0],
      price: '0',
      quantity: 1
    },
    mode: 'onChange'
  })

  const formClassId = watch('classId')

  const minIV = watch('minIV')

  const mainSkillId = watch('mainSkillId')

  const otherSkillId1 = watch('otherSkillId1')

  const otherSkillId2 = watch('otherSkillId2')

  const price = watch('price')

  const quantity = watch('quantity')

  const { setSnackInfo } = useMessageContext()

  const { getApeironPrice, getCurrency } = useCurrencyContext()

  const { fromWei, toWei } = useWeb3()

  const { t } = useTranslation()

  const isMobile = useMediaQuery(theme.breakpoints.down('res768'))

  const { account } = useAccountContext()

  const { createBounty } = useBounty()

  const currency = watch('currency', supportedCurrencies[0])

  const minMarkedPrice = useMemo(() => {
    const amount = R.find(
      R.propEq(currency, 'currencyCode'),
      config?.bounty.apostle.postSetting || []
    )

    return getApeironPrice(currency, amount?.minPrice)
  }, [config?.bounty.apostle.postSetting, currency, getApeironPrice])

  useEffect(() => {
    if (defaultCreateFormValues) {
      reset(defaultCreateFormValues)
    }

    setValue('price', minMarkedPrice)

    // reset is not needed as a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCreateFormValues])

  const handleClose = useCallback(() => {
    setDefaultCreateFormValues({
      classId: null,
      minIV: 0,
      mainSkillId: null,
      otherSkillId1: null,
      otherSkillId2: null,
      mainSkillMinLevel: null,
      otherSkillMinLevel1: null,
      otherSkillMinLevel2: null,
      quantity: 1,
      price: minMarkedPrice,
      currency: supportedCurrencies[0]
    })
    reset()
    setIsCreateDialogOpen(false)
  }, [
    minMarkedPrice,
    reset,
    setDefaultCreateFormValues,
    setIsCreateDialogOpen,
    supportedCurrencies
  ])

  const onSubmit = async (data: CreateBountyFormInputs) => {
    const getSkillId = (skillKey: string | null) => {
      return skillKey ? skillKey.slice(-2) : '0'
    }

    const currency = getCurrency(data.currency)

    const price = toWei(data.price, currency?.decimals || 18)

    const success = await createBounty({
      classId: data.classId || 0,
      mainSkillId: getSkillId(data.mainSkillId),
      otherSkillId1: getSkillId(data.otherSkillId1),
      otherSkillId2: getSkillId(data.otherSkillId2),
      minMainSkillLevel: data.mainSkillMinLevel || 0,
      minOtherSkill1Level: data.otherSkillMinLevel1 || 0,
      minOtherSkill2Level: data.otherSkillMinLevel2 || 0,
      minIV: data.minIV,
      numberToBuy: data.quantity,
      pricePerApostle: price,
      currency: data.currency
    })

    if (success) {
      setSnackInfo({
        message: (
          <Trans
            components={{
              Link: <CheckHereLink href={ROUTE.INVENTORY_BOUNTY_ACTIVE_LIST} />
            }}
          >
            {t('contract.bounty.post.success')}
          </Trans>
        ),
        serverity: 'success'
      })
      handleClose()
      refetchBounties()
    }
  }

  const handleClassChange = useCallback(
    (value: ApostleClassType) => {
      setValue('classId', toApostleClassId(value))
      setValue('mainSkillId', null)
      setValue('otherSkillId1', null)
      setValue('otherSkillId2', null)
      setValue('mainSkillMinLevel', null)
      setValue('otherSkillMinLevel1', null)
      setValue('otherSkillMinLevel2', null)
    },
    [setValue]
  )

  const handleMinIVChange = useCallback(
    (event: any, value: number | number[]) => {
      if (typeof value === 'number') {
        setValue('minIV', value)
      } else if (Array.isArray(value)) {
        setValue('minIV', value[0])
      }
    },
    [setValue]
  )

  const handleMainSkillChange = useCallback(
    (value: ApostleSkill) => {
      setValue('mainSkillId', value.uniqueKey)

      if (otherSkillId1 === value.uniqueKey) {
        setValue('otherSkillId1', null)
      } else if (otherSkillId2 === value.uniqueKey) {
        setValue('otherSkillId2', null)
      }
    },
    [otherSkillId1, otherSkillId2, setValue]
  )

  const handleOtherSkill1Change = useCallback(
    (value: ApostleSkill) => {
      setValue('otherSkillId1', value.uniqueKey)

      if (mainSkillId === value.uniqueKey) {
        setValue('mainSkillId', null)
      } else if (otherSkillId2 === value.uniqueKey) {
        setValue('otherSkillId2', null)
      }
    },
    [mainSkillId, otherSkillId2, setValue]
  )

  const handleOtherSkill2Change = useCallback(
    (value: ApostleSkill) => {
      setValue('otherSkillId2', value.uniqueKey)

      if (mainSkillId === value.uniqueKey) {
        setValue('mainSkillId', null)
      } else if (otherSkillId1 === value.uniqueKey) {
        setValue('otherSkillId1', null)
      }
    },
    [mainSkillId, otherSkillId1, setValue]
  )

  const isFormDisabled = formClassId == null

  const totalPrice = useMemo(() => {
    try {
      return parseFloat(price) * quantity
    } catch {
      return 0
    }
  }, [price, quantity])

  const balance = useMemo(() => {
    const { amount = '0', decimals = 1 } = account?.balances?.[currency] || {}

    return fromWei(amount || '', decimals)
  }, [account?.balances, currency, fromWei])

  const handleCurrencyChange = useCallback(
    (value: CurrencyTokenTypeERC20) => {
      const amount = R.find(
        R.propEq(value, 'currencyCode'),
        config?.bounty.apostle.postSetting || []
      )

      const minPrice = getApeironPrice(value, amount?.minPrice)

      setValue('price', minPrice)
      setValue('currency', value)
    },
    [config, getApeironPrice, setValue]
  )

  return (
    <Popup
      type={INFO_POPUP_TYPE.BLACK}
      open={isCreateDialogOpen}
      onClose={handleClose}
    >
      <Container>
        <>
          <Row>
            <FormLabel>{t('bounty.apostle.create_dialog.class')}</FormLabel>
            <RightCol>
              <ApostleClassSelector
                selectedValue={toApostleClassType(formClassId)}
                onChange={handleClassChange}
              />
            </RightCol>
          </Row>
          <Row>
            <FormLabel>{t('bounty.apostle.create_dialog.iv')}</FormLabel>
            <RightCol>
              <StyledSlider
                value={minIV}
                min={0}
                max={100}
                onChange={handleMinIVChange}
                marks={[
                  {
                    value: 50,
                    label: minIV
                  }
                ]}
                disabled={isFormDisabled}
              />
            </RightCol>
          </Row>
          <Row className='mobile-full'>
            <FormLabel>
              {t('bounty.apostle.create_dialog.first_skill')}
              {mainSkillId && isMobile && (
                <MobileResetButton
                  onClick={() => {
                    setValue('mainSkillId', null)
                    setValue('mainSkillMinLevel', null, {
                      shouldValidate: true
                    })
                  }}
                >
                  {t('common.button.reset')}
                </MobileResetButton>
              )}
            </FormLabel>
            <RightCol>
              <Box>
                <FlexBox>
                  <ApostleSkillSelector
                    classType={toApostleClassType(formClassId)}
                    skillType={ApostleSkillType.ActiveSkill}
                    selectedValue={mainSkillId}
                    onChange={handleMainSkillChange}
                    hiddenSkillIds={[otherSkillId1, otherSkillId2]}
                    disabled={isFormDisabled}
                  />
                  <SkillMinLevelField
                    control={control}
                    name='mainSkillMinLevel'
                    disabled={isFormDisabled || !mainSkillId}
                  />
                  {mainSkillId && !isMobile && (
                    <ResetButton
                      onClick={() => {
                        setValue('mainSkillId', null)
                        setValue('mainSkillMinLevel', null, {
                          shouldValidate: true
                        })
                      }}
                    >
                      {t('common.button.reset')}
                    </ResetButton>
                  )}
                </FlexBox>
                <ErrorText>{errors.mainSkillMinLevel?.message}</ErrorText>
              </Box>
            </RightCol>
          </Row>
          <Row className='mobile-full'>
            <FormLabel>
              {t('bounty.apostle.create_dialog.other_skills')}
              {(otherSkillId1 || otherSkillId2) && isMobile && (
                <MobileResetButton
                  onClick={() => {
                    setValue('otherSkillId1', null)
                    setValue('otherSkillMinLevel1', null, {
                      shouldValidate: true
                    })
                    setValue('otherSkillId2', null)
                    setValue('otherSkillMinLevel2', null, {
                      shouldValidate: true
                    })
                  }}
                >
                  {t('common.button.reset')}
                </MobileResetButton>
              )}
            </FormLabel>
            <RightCol>
              <Box>
                <FlexBox>
                  <ApostleSkillSelector
                    classType={toApostleClassType(formClassId)}
                    skillType={ApostleSkillType.ActiveSkill}
                    selectedValue={otherSkillId1}
                    onChange={handleOtherSkill1Change}
                    disabled={isFormDisabled}
                    hiddenSkillIds={[mainSkillId, otherSkillId2]}
                  />
                  <SkillMinLevelField
                    control={control}
                    name='otherSkillMinLevel1'
                    disabled={isFormDisabled || !otherSkillId1}
                  />
                  {otherSkillId1 && !isMobile ? (
                    <ResetButton
                      onClick={() => {
                        setValue('otherSkillId1', null)
                        setValue('otherSkillMinLevel1', null, {
                          shouldValidate: true
                        })
                      }}
                    >
                      {t('common.button.reset')}
                    </ResetButton>
                  ) : null}
                </FlexBox>
                <ErrorText>{errors.otherSkillMinLevel1?.message}</ErrorText>
              </Box>
              <Box>
                <FlexBox>
                  <ApostleSkillSelector
                    classType={toApostleClassType(formClassId)}
                    skillType={ApostleSkillType.ActiveSkill}
                    selectedValue={otherSkillId2}
                    onChange={handleOtherSkill2Change}
                    disabled={isFormDisabled}
                    hiddenSkillIds={[mainSkillId, otherSkillId1]}
                  />

                  <SkillMinLevelField
                    control={control}
                    name='otherSkillMinLevel2'
                    disabled={isFormDisabled || !otherSkillId2}
                  />
                  {otherSkillId2 && !isMobile && (
                    <ResetButton
                      onClick={() => {
                        setValue('otherSkillId2', null)
                        setValue('otherSkillMinLevel2', null, {
                          shouldValidate: true
                        })
                      }}
                    >
                      {t('common.button.reset')}
                    </ResetButton>
                  )}
                </FlexBox>
                <ErrorText>{errors.otherSkillMinLevel2?.message}</ErrorText>
              </Box>
            </RightCol>
          </Row>
          <Row>
            <FormLabel>{t('bounty.apostle.create_dialog.quantity')}</FormLabel>
            <RightCol>
              <StyledApeironTextField
                control={control}
                disabled={isFormDisabled}
                name='quantity'
                reserveHelperTextSpace={false}
                rules={{
                  required: {
                    value: true,
                    message: t('common.form_validation.required')
                  },
                  max: {
                    value: MAX_APOSTLE_QUANTITY,
                    message: t('common.form_validation.max', {
                      max: MAX_APOSTLE_QUANTITY
                    })
                  },
                  min: {
                    value: 1,
                    message: t('common.form_validation.min', {
                      min: 1
                    })
                  },
                  pattern: {
                    value: REGEX_WHOLE_NUMBER,
                    message: t('common.form_validation.number_pattern')
                  }
                }}
                helperRemark={
                  errors.quantity ? (
                    <ErrorText>{errors.quantity?.message}</ErrorText>
                  ) : (
                    <MaxHelperText />
                  )
                }
                placeholder={'1'}
              />
            </RightCol>
          </Row>
          <Separator />
          <Row>
            <FormLabel>{t('bounty.apostle.create_dialog.set_price')}</FormLabel>
            <RightCol>
              <StyledApeironTextField
                control={control}
                disabled={isFormDisabled}
                name='price'
                reserveHelperTextSpace={false}
                rules={{
                  required: {
                    value: true,
                    message: t('common.form_validation.required')
                  },
                  max: {
                    value: balance,
                    message: t(
                      'bounty.apostle.create_dialog.not_enough_balance',
                      {
                        balance: formatNumberString(balance)
                      }
                    )
                  },
                  min: {
                    value: minMarkedPrice,
                    message: t('common.form_validation.min', {
                      min: minMarkedPrice
                    })
                  },
                  pattern: {
                    value: REGEX_NUMBER,
                    message: t('common.form_validation.number_pattern')
                  }
                }}
                helperRemark={
                  errors.price ? (
                    <ErrorText>{errors.price?.message}</ErrorText>
                  ) : (
                    <TotalPrice>
                      <Trans
                        components={{
                          strong: <strong />
                        }}
                      >
                        {t('bounty.apostle.create_dialog.total_price', {
                          price: totalPrice || minMarkedPrice
                        })}
                      </Trans>
                    </TotalPrice>
                  )
                }
                placeholder={'0'}
                endAdornment={
                  supportedCurrencies.length > 1 ? (
                    <BountyCurrencyDropdown
                      value={currency}
                      options={supportedCurrencies as CurrencyTokenTypeERC20[]}
                      onChange={handleCurrencyChange}
                      disabled={isFormDisabled}
                    />
                  ) : (
                    <CurrencyNameText>{currency}</CurrencyNameText>
                  )
                }
              />
            </RightCol>
          </Row>
          <ActionButtonsContainer>
            <CancelButton onClick={handleClose}>
              {t('common.button.cancel')}
            </CancelButton>
            <StyledActionButton
              onClick={handleSubmit(onSubmit)}
              disabled={
                isFormDisabled ||
                R.isNil(formClassId) ||
                R.isNotNil(errors.quantity) ||
                R.isNotNil(errors.price)
              }
            >
              {t('bounty.apostle.create_dialog.post_bounty')}
            </StyledActionButton>
          </ActionButtonsContainer>
        </>
      </Container>
    </Popup>
  )
}

export type CreateBountyFormInputs = {
  classId: ApostleClassId | null
  minIV: number
  mainSkillId: string | null
  otherSkillId1: string | null
  otherSkillId2: string | null
  mainSkillMinLevel: number | null
  otherSkillMinLevel1: number | null
  otherSkillMinLevel2: number | null
  quantity: number
  price: string
  currency: CurrencyTokenTypeERC20
}

export default CreateApostleBountyDialog
